import { useEffect, useState } from 'react'
import type { ReportTheme } from '../../types/reportTheme'
import { listThemes } from './reportThemesApi'
import { SelectField } from '../reports/config/controls'

interface ThemeSelectProps {
  value: number | null
  onChange: (themeId: number | null) => void
  label?: string
  /** Rend un select nu (sans label empilé au-dessus) — pour les barres d'outils. */
  hideLabel?: boolean
}

const NONE = ''

/**
 * Sélecteur de thème réutilisable (réglages rapport, fiche client). Charge les
 * thèmes du user au montage et expose une option « Aucun » (rendu neutre).
 */
export function ThemeSelect({ value, onChange, label = 'Thème de rapport', hideLabel = false }: ThemeSelectProps) {
  const [themes, setThemes] = useState<ReportTheme[]>([])

  useEffect(() => {
    let alive = true
    listThemes()
      .then((data) => { if (alive) setThemes(Array.isArray(data) ? data : []) })
      .catch(() => { if (alive) setThemes([]) })
    return () => { alive = false }
  }, [])

  const options = [
    { value: NONE, label: 'Aucun (rendu neutre)' },
    ...themes.map((t) => ({ value: String(t.id), label: t.name })),
  ]

  const selectValue = value == null ? NONE : String(value)
  const handleChange = (v: string) => onChange(v === NONE ? null : Number(v))

  // Variante barre d'outils : select nu (sans label empilé) pour s'aligner avec les
  // boutons voisins. Le label reste accessible via `aria-label`.
  if (hideLabel) {
    return (
      <select
        aria-label={label}
        value={selectValue}
        onChange={(e) => handleChange(e.target.value)}
        className="h-9 rounded-lg border border-[#E2E4ED] bg-white px-2 text-sm text-[#1B2559] focus:border-[#6366F1] focus:outline-none"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    )
  }

  return (
    <SelectField
      label={label}
      value={selectValue}
      options={options}
      onChange={handleChange}
    />
  )
}
